import {
    QueryClient,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { auditLogQueryKeys } from "@api/auditLog.api";
import { invalidateOrganizationHistory } from "@api/organizationAuditLog.api";
import {
    coaTemplateApplyEventQueryKeys,
    undoCoaTemplateApplyEvent,
} from "@api/coaTemplateApplyEvent.api";
import type { CoaTemplateActivityEventRef } from "./coaTemplateActivityEvents";
import {
    CoaTemplateGroupedUndoResult,
    runCoaTemplateGroupedUndo,
} from "./coaTemplateGroupedUndo";

interface GroupedUndoVariables {
    eventRefs: CoaTemplateActivityEventRef[];
    undoInvocationId: string;
}

export async function refreshCoaTemplateGroupedUndoCaches({
    queryClient,
    templateActivityQueryKey,
}: {
    queryClient: QueryClient;
    templateActivityQueryKey?: readonly string[];
}) {
    // Critical: this drives the rail's reverted state and Undo selectability. The
    // activity aggregate and every per-event detail/eligibility entry share the
    // all() prefix, so one invalidation covers them without double-refetching.
    // Run it first so a later refetch rejection can't leave already-reverted
    // entities selectable.
    await queryClient.invalidateQueries({
        queryKey: coaTemplateApplyEventQueryKeys.all(),
    });
    invalidateOrganizationHistory(queryClient);

    // Feed + template-activity refetches are best-effort and independent: they run
    // together so one rejection never stops the other from refreshing, and the
    // combined failure is reported only after both have settled.
    //
    // `throwOnError` is what makes a failing query observable here: without it
    // refetchQueries resolves even when the underlying request fails, so a stale
    // Activity feed would look like a clean refresh.
    const refreshes = await Promise.allSettled([
        queryClient.refetchQueries(
            { queryKey: auditLogQueryKeys.all },
            { throwOnError: true },
        ),
        ...(templateActivityQueryKey
            ? [
                  queryClient.refetchQueries(
                      { queryKey: templateActivityQueryKey },
                      { throwOnError: true },
                  ),
              ]
            : []),
    ]);

    if (refreshes.some((refresh) => refresh.status === "rejected")) {
        throw new Error("Activity refresh failed after undo");
    }
}

export function useCoaTemplateGroupedUndo({
    templateActivityQueryKey,
}: {
    templateActivityQueryKey?: readonly string[];
} = {}) {
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();

    return useMutation({
        mutationFn: ({ eventRefs, undoInvocationId }: GroupedUndoVariables) =>
            runCoaTemplateGroupedUndo({
                eventRefs,
                undoInvocationId,
                undoEvent: undoCoaTemplateApplyEvent,
            }),
        onSuccess: async (_results: CoaTemplateGroupedUndoResult[]) => {
            try {
                await refreshCoaTemplateGroupedUndoCaches({
                    queryClient,
                    templateActivityQueryKey,
                });
            } catch {
                toastError(
                    "Undo completed, but activity failed to refresh. Reload the page.",
                );
            }
        },
    });
}
